import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Leaf, Coins, Sparkles, Users, Clock, Video, ShieldCheck, TrendingUp, Wind } from "lucide-react";
import { PILLARS, TID, TESTIMONIALS, GOOGLE_REVIEWS_URL, WHATSAPP_URL, ASSET, CONSULTATION, DEVELOPER, AQI_INDEX, AMENITIES, HOME_CONNECTIVITY_TILES } from "@/constants/testIds";
import RegistrationsTicker from "@/components/RegistrationsTicker";
import MapLocation from "@/components/MapLocation";

const pillarIcons = {
  nature: Leaf,
  wealth: Coins,
  mind: Sparkles,
  family: Users,
  time: Clock,
};

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section
        className="relative min-h-[92vh] flex items-end overflow-hidden"
        style={{ background: "var(--phc-black)" }}
      >
        <img
          src={ASSET.heroSarasgad}
          alt="Sarasgad ridge at golden hour — the view from Chapter III"
          className="absolute inset-0 w-full h-full object-cover opacity-95 breathe"
          fetchPriority="high"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={(e) => {
            if (!e.currentTarget.dataset.fallback) {
              e.currentTarget.dataset.fallback = "1";
              e.currentTarget.src = ASSET.heroSarasgadOrig;
            }
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-black/5" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-transparent to-transparent" />
        <div className="relative z-10 container-editorial px-5 md:px-10 pb-16 md:pb-24 pt-32 w-full">
          <div className="max-w-3xl fade-up">
            <div className="eyebrow text-lime mb-6">
              <span className="editorial-rule bg-lime" />
              Villa Plots Near Mumbai &amp; Pune · Chapter III
            </div>
            <h1 className="font-display text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight drop-shadow-[0_2px_20px_rgba(0,0,0,0.4)]">
              The Peace<br />
              <span className="italic text-cream/95">of Land.</span>
              <span className="sr-only">
                Pali Hill City — Nature-centric N.A. villa plots for weekend homes near Mumbai and Pune.
              </span>
            </h1>
            <p className="mt-8 text-white text-base md:text-lg max-w-xl leading-relaxed drop-shadow-[0_1px_10px_rgba(0,0,0,0.6)]">
              A 300-acre private hill near Pali, Raigad. Collector-approved
              Non-Agricultural residential <strong>villa plots</strong>, MahaRERA
              registered — the second-home destination two hours from Mumbai,
              two hours from Pune, and 1.5 hours from the upcoming Navi Mumbai
              International Airport.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Link data-testid={TID.heroBookVisit} to="/event" className="btn-primary">
                Register for Launch Event <ArrowUpRight size={17} />
              </Link>
              <Link data-testid={TID.heroStrategy} to="/strategy-call" className="btn-outline-cream">
                <Video size={16} /> Free Online Consultation
              </Link>
            </div>
            <p className="mt-5 text-xs text-cream/70 tracking-wide">
              Sept 12–13, 2026 · At the ridge · Get your boarding pass + a spin-to-win.
            </p>
          </div>
        </div>

        {/* micro credit strip */}
        <div className="absolute bottom-6 right-6 z-10 text-[10px] text-cream/50 hidden md:block">
          Sarasgad ridge · Golden hour · Pali
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section id="philosophy" className="section-pad bg-offwhite relative overflow-hidden">
        <div className="container-editorial grid md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <div className="eyebrow text-teal mb-4">
              <span className="editorial-rule" />
              The Philosophy
            </div>
            <h2 className="font-display text-forest text-3xl md:text-4xl leading-tight">
              A worldview,<br />not a pitch.
            </h2>
          </div>
          <div className="md:col-span-8 md:col-start-5">
            <p className="dropcap font-display text-forest text-2xl md:text-[28px] leading-snug italic">
              Peace is not the absence of noise. It is the presence of the
              things you actually chose. Chapter III is a five-part reading of
              what "peace" means when you own a piece of land — with nature,
              with wealth, with mind, with family, with time.
            </p>
            <p className="mt-6 text-forest/80 text-base leading-relaxed max-w-2xl">
              Below are the five pillars. Each one is a hub — a longer read, a
              deeper reason, and a way to see whether Pali Hill City actually
              fits the life you'd like to live in twenty years.
            </p>
            <div className="mt-8 flex items-center gap-4">
              <Link to="/pillars/nature" className="btn-outline-forest text-sm">
                Read the philosophy <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ONLINE CONSULTATION STRIP */}
      <section className="relative bg-forest text-cream py-16 md:py-24 overflow-hidden">
        <div
          className="absolute inset-0 opacity-25 pointer-events-none"
          style={{
            backgroundImage: `url('${ASSET.cSector}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-forest via-forest/85 to-forest/40 pointer-events-none" />
        <div className="relative container-editorial px-5 md:px-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="eyebrow text-lime mb-4">
              <span className="editorial-rule bg-lime" /> Online Consultation · Free · 30 min
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
              Talk to us <span className="italic">before</span> you visit.
            </h2>
            <p className="mt-6 text-cream/80 text-base md:text-lg leading-relaxed max-w-xl">
              A one-on-one Google Meet with our senior sales advisor. Pick a
              time that suits you — we <span className="text-lime font-semibold">add the meeting to your Google Calendar
              instantly</span>, with a Meet link included. Ask anything about
              paperwork, pricing, or the plot map.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/strategy-call" className="btn-primary">
                <Video size={16} /> Book Online Consultation
              </Link>
              <span className="inline-flex items-center gap-2 text-xs text-cream/60">
                <ShieldCheck size={14} className="text-lime" /> No obligation · No follow-up spam
              </span>
            </div>
          </div>
          <div className="md:col-span-5 grid grid-cols-2 gap-3">
            {[
              { v: "30 min", l: "One-on-one Google Meet" },
              { v: "Instant", l: "Google Calendar invite" },
              { v: "Live", l: "Advisor + plot map on screen" },
              { v: "0 spam", l: "No automated call loops" },
            ].map((s) => (
              <div key={s.l} className="border border-cream/20 bg-cream/[0.03] p-5 rounded-lg backdrop-blur-sm">
                <div className="font-display text-3xl md:text-4xl leading-none">{s.v}</div>
                <div className="eyebrow text-cream/60 mt-3 leading-snug">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FIVE PILLARS GRID */}
      <section className="pt-24 md:pt-40 pb-24 md:pb-40 bg-offwhite">
        <div className="container-editorial px-5 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-6">
            {PILLARS.map((p, i) => {
              const Icon = pillarIcons[p.slug] || Leaf;
              // asymmetric bento: first big, others varied
              const spans = [
                "md:col-span-3 md:row-span-2 md:h-[520px]",
                "md:col-span-3 md:h-[250px]",
                "md:col-span-2 md:h-[250px]",
                "md:col-span-2 md:h-[260px]",
                "md:col-span-2 md:h-[260px]",
              ];
              return (
                <Link
                  to={`/pillars/${p.slug}`}
                  key={p.slug}
                  data-testid={`pillar-card-${p.slug}`}
                  className={`pillar-card group h-[280px] ${spans[i]} relative`}
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  <img
                    src={p.image}
                    alt={p.label}
                    loading="eager"
                    decoding="async"
                    referrerPolicy="no-referrer"
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => {
                      if (!e.currentTarget.dataset.fallback) {
                        e.currentTarget.dataset.fallback = "1";
                        e.currentTarget.src = ASSET.heroSarasgad;
                      }
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                  <div className="relative h-full p-6 md:p-7 flex flex-col justify-between text-cream">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full grid place-items-center bg-lime/90 text-void">
                        <Icon size={16} />
                      </span>
                      <span className="eyebrow text-cream/80">Pillar {i + 1}</span>
                    </div>
                    <div>
                      <h3 className={`font-display leading-tight ${i === 0 ? "text-3xl md:text-5xl" : "text-2xl md:text-3xl"}`}>
                        {p.label}
                      </h3>
                      <p className="mt-2 text-cream/80 text-sm max-w-md leading-relaxed">
                        {p.tagline}
                      </p>
                      <div className="mt-4 flex items-center gap-2 text-lime text-xs tracking-widest uppercase">
                        Read the hub <ArrowUpRight size={14} />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROOF OF PEACE STRIP */}
      <section className="bg-forest text-cream section-pad relative overflow-hidden">
        <div className="container-editorial grid md:grid-cols-12 gap-10 relative z-10">
          <div className="md:col-span-4">
            <div className="eyebrow text-lime mb-4">
              <span className="editorial-rule bg-lime" /> Trust & Track Record
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">
              Hear what our past<br />customers have to say.
            </h2>
            <p className="mt-6 text-cream/75 leading-relaxed">
              Verified reviews from plot owners at Pali Hill City and other
              layouts developed by our team &mdash; pulled from public Google
              Maps and Housing.com listings. The same team, same paperwork
              discipline, now bringing Chapter III to Pali.
            </p>
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="mt-6 inline-flex items-center gap-2 text-lime text-sm"
            >
              Read all Google reviews <ArrowRight size={16} />
            </a>
          </div>

          <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <article
                key={i}
                className="bg-white/95 text-forest rounded-2xl p-5 md:p-6 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.5)] flex flex-col"
              >
                {/* Google header */}
                <header className="flex items-center gap-3 pb-3 border-b border-forest/10">
                  <div className="w-9 h-9 rounded-full grid place-items-center bg-forest text-cream font-display text-sm">
                    {t.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                  </div>
                  <div className="min-w-0">
                    <div className="text-forest text-sm font-semibold leading-tight truncate">
                      {t.name}
                    </div>
                    <div className="text-forest/50 text-[11px] flex items-center gap-1.5">
                      <svg width="12" height="12" viewBox="0 0 24 24" aria-label="Google">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      {t.when}
                    </div>
                  </div>
                </header>

                {/* Star rating */}
                <div className="flex items-center gap-1 mt-3">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <svg
                      key={s}
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill={s < (t.rating || 5) ? "#FBBC05" : "#e5e7eb"}
                      aria-hidden="true"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ))}
                </div>

                {/* Quote */}
                <p className="mt-3 text-forest/80 text-[13px] md:text-sm leading-relaxed flex-1">
                  {t.quote}
                </p>

                <footer className="mt-4 pt-3 border-t border-forest/10 text-[11px] text-forest/55">
                  {t.role}
                </footer>
              </article>
            ))}
          </div>
        </div>

        {/* Editorial contour lines background */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.06] pointer-events-none" viewBox="0 0 1440 800" preserveAspectRatio="none">
          {Array.from({ length: 12 }).map((_, i) => (
            <path key={i} d={`M0,${60 + i * 55} Q360,${30 + i * 55} 720,${80 + i * 55} T1440,${60 + i * 55}`} fill="none" stroke="#F4F4EF" strokeWidth="1" />
          ))}
        </svg>
      </section>

      {/* THE DEVELOPER — MALPANI INFRA */}
      <section className="section-pad bg-offwhite">
        <div className="container-editorial px-5 md:px-10 grid md:grid-cols-12 gap-10">
          <aside className="md:col-span-4">
            <div className="eyebrow text-teal mb-4">
              <span className="editorial-rule" /> The Developer
            </div>
            <div className="inline-flex items-center gap-4 mb-6">
              <img
                src="/malpani-logo-black.webp"
                alt="Malpani Infra logo"
                width={140}
                height={44}
                className="h-10 md:h-12 w-auto select-none"
                draggable="false"
              />
              <div className="leading-tight border-l border-forest/20 pl-4">
                <div className="font-display text-forest text-xl leading-tight">{DEVELOPER.name}</div>
                <div className="eyebrow text-forest/60">{DEVELOPER.tagline}</div>
              </div>
            </div>
            <h2 className="font-display text-forest text-3xl md:text-4xl leading-tight">
              Four decades of<br />quietly delivered land.
            </h2>
          </aside>

          <div className="md:col-span-8 space-y-8">
            <p className="text-forest/85 text-lg leading-relaxed max-w-3xl">
              {DEVELOPER.intro}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="border-l-2 border-forest pl-4">
                <div className="font-display text-forest text-4xl md:text-5xl leading-none">{DEVELOPER.legacyYears}</div>
                <div className="eyebrow text-forest/60 mt-3">Years of legacy</div>
              </div>
              <div className="border-l-2 border-forest pl-4">
                <div className="font-display text-forest text-4xl md:text-5xl leading-none">{DEVELOPER.sqftInMotion}</div>
                <div className="eyebrow text-forest/60 mt-3">Sq. ft. in motion</div>
              </div>
              <div className="border-l-2 border-forest pl-4">
                <div className="font-display text-forest text-4xl md:text-5xl leading-none">II</div>
                <div className="eyebrow text-forest/60 mt-3">Phases sold out</div>
              </div>
              <div className="border-l-2 border-lime pl-4">
                <div className="font-display text-forest text-4xl md:text-5xl leading-none">III</div>
                <div className="eyebrow text-forest/60 mt-3">Chapter live now</div>
              </div>
            </div>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 pt-2">
              {DEVELOPER.values.map((v, i) => (
                <li key={i} className="flex items-start gap-3 text-forest/85 text-[15px] leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal mt-2 shrink-0" />
                  {v}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* AIR QUALITY — dramatic hero-style */}
      <section className="relative bg-void text-cream py-24 md:py-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-45"
          style={{ backgroundImage: `url('${ASSET.heroSarasgad}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/70 to-black/40 pointer-events-none" />
        <div className="relative container-editorial px-5 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="eyebrow text-lime mb-4">
              <span className="editorial-rule bg-lime" /> The Air, First
            </div>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.98]">
              <span className="italic text-cream/85">A ridge</span><br />
              guarded by<br />forest on three sides.
            </h2>
            <p className="mt-6 text-cream/80 leading-relaxed max-w-md text-lg">
              Chapter III sits on a hilltop protected by dense forest on three of
              its four sides. That's not a landscaping choice — it's a natural
              buffer that keeps the air fresh year-round. The number in the
              chart below is what your lungs will actually meet.
            </p>
            <div className="mt-8 flex items-baseline gap-3">
              <div className="font-display text-lime text-[7rem] md:text-[10rem] leading-none tracking-tight">
                54
              </div>
              <div>
                <div className="eyebrow text-cream/60">AQI · Pali Hills</div>
                <div className="text-lime text-sm font-semibold tracking-wide">Good · fresh year-round</div>
                <div className="text-[10px] text-cream/40 mt-1 tracking-widest uppercase">Source: AQI.IN · Weather Bug</div>
              </div>
            </div>
          </div>

          <div className="md:col-span-7 space-y-2 self-end">
            <div className="eyebrow text-cream/70 mb-4">
              <span className="editorial-rule bg-cream/70" /> Neighbourhood AQI, compared
            </div>
            {AQI_INDEX.map((row) => {
              const best = row.tone === "best";
              const pct = Math.min(100, (row.value / 150) * 100);
              return (
                <div key={row.place} className="flex items-center gap-4">
                  <div className={`w-36 text-sm ${best ? "font-semibold text-lime" : "text-cream/70"}`}>
                    {row.place}
                  </div>
                  <div className="flex-1 h-8 rounded-full bg-cream/10 relative overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-[width] duration-700 ${
                        best ? "bg-gradient-to-r from-lime to-teal" : row.value >= 130 ? "bg-cream/40" : "bg-teal/60"
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                    <div className="absolute inset-0 flex items-center justify-end pr-3 text-[10px] text-cream/60 tracking-widest uppercase">
                      {best ? "Fresh" : row.value >= 130 ? "Poor" : "Moderate"}
                    </div>
                  </div>
                  <div className={`w-12 text-right font-display text-2xl md:text-3xl ${best ? "text-lime" : "text-cream/80"}`}>
                    {row.value}
                  </div>
                </div>
              );
            })}
            <p className="text-[11px] text-cream/50 mt-6 max-w-md leading-relaxed">
              Cleaner air isn't an amenity in a brochure — it's the reason all the
              other amenities exist here at all.
            </p>
          </div>
        </div>
      </section>

      {/* AMENITIES with images */}
      <section className="section-pad bg-offwhite">
        <div className="container-editorial px-5 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <aside className="md:col-span-4">
              <div className="eyebrow text-teal mb-4">
                <span className="editorial-rule" /> The Gymkhana
              </div>
              <h2 className="font-display text-forest text-3xl md:text-5xl leading-tight">
                Members-only.<br />Old-English style.<br />New-hill setting.
              </h2>
              <p className="mt-6 text-forest/70 leading-relaxed max-w-md">
                Every Chapter III plot buyer becomes a member of the Pali Hills
                Gymkhana — a private club and grounds inherited from the master
                layout. Eight amenities. One membership. Every weekend.
              </p>
            </aside>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
            {AMENITIES.map((a, i) => {
              // First tile spans wider on md+ for editorial rhythm
              const wide = i === 0;
              return (
                <div
                  key={a.label}
                  className={`group relative overflow-hidden rounded-lg bg-forest ${
                    wide ? "sm:col-span-2 md:col-span-2 md:row-span-1 h-[280px] md:h-[340px]" : "h-[240px] md:h-[260px]"
                  }`}
                >
                  <img
                    src={a.image}
                    alt={a.label}
                    width="1200"
                    height="800"
                    loading="eager"
                    decoding="async"
                    referrerPolicy="no-referrer"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => {
                      // Graceful fallback: use the Sarasgad hero if a stock URL fails
                      if (!e.currentTarget.dataset.fallback) {
                        e.currentTarget.dataset.fallback = "1";
                        e.currentTarget.src = "https://customer-assets.emergentagent.com/job_pali-hill-new/artifacts/3wclv5hn_Sarasgad_Golden_hour_sunset.png";
                      }
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                  <div className="absolute inset-0 p-5 md:p-6 flex flex-col justify-end text-cream">
                    <div className={`font-display leading-tight ${wide ? "text-2xl md:text-3xl" : "text-lg md:text-xl"}`}>
                      {a.label}
                    </div>
                    <p className="text-cream/75 text-xs md:text-sm mt-1.5 leading-snug max-w-xs">
                      {a.detail}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PARALLAX RIDGE — cinematic full-bleed */}
      <section
        className="relative min-h-[70vh] md:min-h-[85vh] w-full overflow-hidden"
        style={{
          backgroundImage: `url('${ASSET.heroSarasgad}')`,
          backgroundAttachment: "fixed",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/50" />
        <div className="relative min-h-[70vh] md:min-h-[85vh] container-editorial px-5 md:px-10 flex items-center">
          <div className="max-w-3xl text-cream">
            <div className="eyebrow text-lime mb-6">
              <span className="editorial-rule bg-lime" /> The Ridge
            </div>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.98]">
              Above the clouds.<br />
              <span className="italic text-cream/90">Below the noise.</span>
            </h2>
            <p className="mt-6 text-cream/85 text-base md:text-lg max-w-xl leading-relaxed">
              Chapter III sits at an elevation where mornings arrive with mist
              you can walk through, and evenings close with a light the city
              has forgotten. Scroll on — the view moves with you.
            </p>
          </div>
        </div>
      </section>

      {/* VIDEO SECTION — a moment on the hill */}
      <section className="section-pad bg-cream">
        <div className="container-editorial px-5 md:px-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-4">
            <div className="eyebrow text-teal mb-4">
              <span className="editorial-rule" /> One Minute at the Site
            </div>
            <h2 className="font-display text-forest text-4xl md:text-5xl leading-tight">
              A minute on<br />the ridge.
            </h2>
            <p className="mt-6 text-forest/70 leading-relaxed max-w-md">
              We shot this without a script. Golden hour, a Sunday, one drone,
              and the quiet the ridge already had.
            </p>
            <div className="mt-6 text-xs text-forest/50 tracking-wide">
              Play with sound on for the birds.
            </div>
          </div>
          <div className="md:col-span-8">
            <div className="relative aspect-video overflow-hidden rounded-xl border border-forest/15 shadow-[0_30px_60px_-30px_rgba(11,74,61,0.35)] bg-void">
              <video
                controls
                preload="metadata"
                playsInline
                poster={ASSET.heroSarasgad}
                className="absolute inset-0 w-full h-full object-cover"
              >
                <source
                  src="https://customer-assets-lxgj4vgw.emergentagent.net/job_pali-hill-new/artifacts/l85ui3ts_PHC%20CH3%20FULL%20TEASER.mp4"
                  type="video/mp4"
                />
                Your browser does not support embedded video.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* CHAPTER III EVENT STRIP */}
      <section className="section-pad bg-offwhite">
        <div className="container-editorial grid md:grid-cols-2 gap-14 items-center">
          <div>
            <div className="eyebrow text-teal mb-4">
              <span className="editorial-rule" /> Chapter III · Sept 12–13, 2026
            </div>
            <h2 className="font-display text-forest text-4xl md:text-5xl leading-tight">
              The launch is above the clouds.
            </h2>
            <p className="mt-6 text-forest/75 leading-relaxed max-w-lg">
              Two golden-hour days at the site. Walk the ridge, meet the
              founders, and see the plot map in the light it was drawn for.
              Guest list is filling — this is what the counter looks like right
              now:
            </p>
            <div className="mt-8">
              <RegistrationsTicker />
            </div>
            <div className="mt-8">
              <Link to="/event" className="btn-primary">
                Reserve my seat — Chapter III <ArrowRight size={16} />
              </Link>
            </div>
          </div>
          <div className="relative aspect-[4/5] overflow-hidden rounded-md">
            <img
              src={ASSET.conceptVilla}
              alt="Concept villa at the Pali Hill City ridge, golden hour"
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
              decoding="async"
              referrerPolicy="no-referrer"
              onError={(e) => {
                if (!e.currentTarget.dataset.fallback) {
                  e.currentTarget.dataset.fallback = "1";
                  e.currentTarget.src = ASSET.conceptVillaOrig;
                }
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-cream">
              <div className="eyebrow text-lime">The Site · Pali Ridge</div>
              <div className="font-display text-2xl mt-1">A quiet ridge, a Saturday, and a plot map.</div>
            </div>
          </div>
        </div>
      </section>

      {/* CONNECTIVITY MAP MINI */}
      <section className="section-pad bg-cream relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=2000&q=70&auto=format&fit=crop')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="container-editorial grid md:grid-cols-12 gap-10 relative">
          <div className="md:col-span-4">
            <div className="eyebrow text-teal mb-4">
              <span className="editorial-rule" /> Connectivity
            </div>
            <h2 className="font-display text-forest text-4xl md:text-5xl leading-tight">The map is moving toward Pali.</h2>
            <Link to="/connectivity" className="mt-6 inline-flex items-center gap-2 text-forest text-sm underline underline-offset-4 decoration-lime decoration-2">
              See the full roadmap <ArrowRight size={16} />
            </Link>
          </div>
          <div className="md:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {HOME_CONNECTIVITY_TILES.map((s) => (
              <div key={s.label} className="border border-forest/20 bg-white/70 backdrop-blur-sm p-6 rounded-md">
                <div className="font-display text-forest text-3xl md:text-4xl leading-none">{s.value}</div>
                <div className="eyebrow text-forest/60 mt-3">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MAP LOCATION — before contact / closing CTA */}
      <MapLocation />

      {/* CALL FOOTER */}
      <section className="section-pad bg-cream">
        <div className="container-editorial grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <h2 className="font-display text-forest text-4xl md:text-6xl leading-[1.05]">
              If the paperwork holds,<br />the peace follows.
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 space-y-4">
            <p className="text-forest/70 text-base leading-relaxed">
              Sept 12–13, 2026 at the ridge. Register now for your boarding
              pass, spin the wheel for an event perk, and lock in your seat.
              Just 300 invites open for the two days.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/event" className="btn-primary">
                <ArrowUpRight size={16} /> Register for Launch Event
              </Link>
              <Link to="/strategy-call" className="btn-outline-forest">
                Or an online consultation →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
